import * as RDialog from '@radix-ui/react-dialog';
import { PERMISSIONS as P } from '@igoal/shared';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Container, CornerDownLeft, Package, Search, Ship, type LucideIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate } from '../lib/format';
import { StatusBadge } from './OrderStatus';
import { ShipmentStatusBadge } from './ShipmentStatus';
import { clsx, useDebounced } from './ui';

export interface Destination {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  group: string | null;
  short?: string;
}

interface Hit {
  key: string;
  to: string;
  icon: LucideIcon | ComponentType<{ className?: string }>;
  title: string;
  detail?: string;
  trailing?: React.ReactNode;
  group: 'Go to' | 'Items' | 'Orders' | 'Shipments';
}

/**
 * Cmd/Ctrl+K: jump to a screen, or find an item, order or shipment by name, code or number.
 * Only screens the user can open are listed, and each search runs only with the matching permission.
 */
export function CommandPalette({ open, onOpenChange, destinations }: { open: boolean; onOpenChange: (o: boolean) => void; destinations: Destination[] }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [active, setActive] = useState(0);
  const q = useDebounced(text.trim(), 200);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setText('');
      setActive(0);
    }
  }, [open]);

  const enabled = open && q.length >= 2;
  const items = useQuery({
    queryKey: ['palette', 'items', q],
    queryFn: () => api.items.list({ search: q, limit: 6, active: 'all' }),
    enabled: enabled && can(P.ITEMS_VIEW),
    staleTime: 30_000,
  });
  const orders = useQuery({
    queryKey: ['palette', 'orders', q],
    queryFn: () => api.chinaOrders.list({ search: q, limit: 6 }),
    enabled: enabled && (can(P.CHINA_ORDERS_VIEW) || can(P.CHINA_PORTAL_ACCESS) || can(P.CHINA_ORDERS_RECEIVE)),
    staleTime: 30_000,
  });
  const shipments = useQuery({
    queryKey: ['palette', 'shipments', q],
    queryFn: () => api.shipments.list({ search: q, limit: 6 }),
    enabled: enabled && (can(P.SHIPMENTS_VIEW_ALL) || can(P.SHIPMENTS_VIEW_LOCATION) || can(P.SHIPMENTS_MANAGE) || can(P.CHINA_ORDERS_RECEIVE)),
    staleTime: 30_000,
  });

  const hits = useMemo<Hit[]>(() => {
    const needle = text.trim().toLowerCase();
    const screens: Hit[] = destinations
      .filter((d) => !needle || d.label.toLowerCase().includes(needle) || (d.group ?? '').toLowerCase().includes(needle))
      .slice(0, needle ? 5 : 8)
      .map((d) => ({ key: `go:${d.to}:${d.label}`, to: d.to, icon: d.icon, title: d.label, detail: d.group ?? undefined, group: 'Go to' }));
    if (!enabled) return screens;
    const found: Hit[] = [
      ...(items.data?.data ?? []).map((i) => ({
        key: `item:${i.id}`,
        to: `/items?search=${encodeURIComponent(i.sku)}`,
        icon: Package,
        title: i.name,
        detail: [i.sku, i.supplier?.name].filter(Boolean).join(' · '),
        group: 'Items' as const,
      })),
      ...(orders.data?.data ?? []).map((o) => ({
        key: `order:${o.id}`,
        to: `/orders/${o.id}`,
        icon: Container,
        title: `Order ${o.orderNo}`,
        detail: `${o.supplier.name} · ${o.lineCount} lines`,
        trailing: <StatusBadge status={o.status} />,
        group: 'Orders' as const,
      })),
      ...(shipments.data?.data ?? []).map((s) => ({
        key: `ship:${s.id}`,
        to: `/shipments/${s.id}`,
        icon: Ship,
        title: `Ship ${s.shipNo}`,
        detail: [s.containerNo, s.etaDate ? `ETA ${formatDate(s.etaDate)}` : null].filter(Boolean).join(' · '),
        trailing: <ShipmentStatusBadge status={s.status} />,
        group: 'Shipments' as const,
      })),
    ];
    return [...found, ...screens];
  }, [text, enabled, destinations, items.data, orders.data, shipments.data]);

  useEffect(() => setActive(0), [hits.length, q]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const go = (h: Hit) => {
    onOpenChange(false);
    navigate(h.to);
  };

  const searching = enabled && (items.isFetching || orders.isFetching || shipments.isFetching);
  const groups = (['Items', 'Orders', 'Shipments', 'Go to'] as const).map((g) => ({ g, rows: hits.filter((h) => h.group === g) })).filter((x) => x.rows.length);

  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-40 bg-overlay" />
        <RDialog.Content
          className="fixed inset-x-0 top-0 z-50 flex max-h-[100dvh] flex-col bg-surface shadow-xl focus:outline-none sm:inset-x-auto sm:top-[12vh] sm:left-1/2 sm:max-h-[70vh] sm:w-[min(640px,calc(100vw-2rem))] sm:-translate-x-1/2 sm:rounded-xl sm:border sm:border-line"
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => Math.min(a + 1, hits.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === 'Enter' && hits[active]) {
              e.preventDefault();
              go(hits[active]);
            }
          }}
        >
          <RDialog.Title className="sr-only">Search</RDialog.Title>
          <RDialog.Description className="sr-only">Jump to a screen or find an item, order or shipment</RDialog.Description>
          <div className="flex items-center gap-2.5 border-b border-line px-4">
            <Search className="size-4 shrink-0 text-ink-faint" aria-hidden />
            <input
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Search items, orders, shipments or screens…"
              className="h-13 w-full bg-transparent text-base text-ink outline-none placeholder:text-ink-faint focus-visible:outline-none"
              aria-label="Search"
              role="combobox"
              aria-expanded
              aria-controls="palette-results"
              enterKeyHint="go"
            />
            <RDialog.Close className="rounded-md border border-line px-1.5 py-0.5 text-xs text-ink-faint hover:text-ink sm:block hidden">Esc</RDialog.Close>
            <RDialog.Close className="-mr-2 flex size-tap items-center justify-center text-sm text-ink-soft sm:hidden">Close</RDialog.Close>
          </div>
          <div ref={listRef} id="palette-results" role="listbox" className="flex-1 overflow-y-auto py-2">
            {groups.map(({ g, rows }) => (
              <div key={g} className="mb-1">
                <div className="px-4 pt-1.5 pb-1 text-xs font-medium text-ink-faint">{g}</div>
                {rows.map((h) => {
                  const i = hits.indexOf(h);
                  return (
                    <button
                      key={h.key}
                      role="option"
                      aria-selected={i === active}
                      data-active={i === active}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => go(h)}
                      className={clsx('flex w-full cursor-pointer items-center gap-3 px-4 py-2 text-left max-sm:min-h-tap', i === active ? 'bg-teal-wash/70' : 'hover:bg-paper')}
                    >
                      <h.icon className="size-4 shrink-0 text-ink-soft" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm text-ink">{h.title}</div>
                        {h.detail && <div className="truncate text-xs text-ink-faint">{h.detail}</div>}
                      </div>
                      {h.trailing}
                      {i === active && <CornerDownLeft className="hidden size-3.5 text-ink-faint sm:block" aria-hidden />}
                    </button>
                  );
                })}
              </div>
            ))}
            {enabled && !searching && groups.every((x) => x.g === 'Go to') && (
              <div className="px-4 py-3 text-sm text-ink-soft">No items, orders or shipments match “{q}”.</div>
            )}
            {searching && <div className="px-4 py-2 text-xs text-ink-faint">Searching…</div>}
            {!enabled && text.trim().length === 1 && <div className="px-4 py-2 text-xs text-ink-faint">Type one more character to search records.</div>}
          </div>
          <div className="hidden items-center gap-4 border-t border-line px-4 py-2 text-xs text-ink-faint sm:flex">
            <span>↑↓ to move</span>
            <span className="flex items-center gap-1">
              <CornerDownLeft className="size-3" /> to open
            </span>
            <span className="ml-auto flex items-center gap-1">
              <ArrowRight className="size-3" /> Items, orders and shipments need two or more characters
            </span>
          </div>
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

/** true on macOS-like platforms, to show ⌘ instead of Ctrl */
export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
